import { Component, HostBinding } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { forkJoin, of, switchMap } from 'rxjs'
import { IDropdownOption } from 'src/app/shared/models/dropdown-option.interface'
import { DvmFeedbackRow } from 'src/app/shared/models/dvm-row'
import { DVM, DVMPlan, DVMPlanClient, DVMToolSelectedOption, DVMToolSelectionType } from 'src/app/shared/services/ppv.api'
import { DVM_TOOL_JSON } from './dvm'
import { VcRoundedCardComponent } from 'src/app/shared/components/rounded-card/rounded-card.component'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ToastrService } from 'ngx-toastr'
import { MatDialog } from '@angular/material/dialog'
import { BookingModalComponent } from 'src/app/shared/components/booking-modal/booking-modal.component'
import { MeetingSchedulerModalComponent } from 'src/app/shared/components/meeting-scheduler-modal/meeting-scheduler-modal.component'

dayjs.extend(utc)

@Component({
   selector: 'dvm-tool-plan',
   templateUrl: './dvm-tool-plan.component.html',
   styleUrls: ['./dvm-tool-plan.component.scss'],
   standalone: false,
})
export class DVMToolPlanComponent {
   @HostBinding('class.full-flex-height') fullFlexHeight = true

   dvmId: string = ''
   public isDVMToolAdmin = false
   public isRM = false

   public dvmPlan: DVMPlan = null
   public dvm: DVM = {}

   public dvmPlans: DVMPlan[] = []

   selectedCommunicationPreferences: string[] = []
   selectedYearsExperience: string | null = null
   selectedLendAHand: boolean | null = null

   emailInputValue: string = ''
   phoneNumberInputValue: string = ''

   interestsTextValue: string = ''
   concernsTextValue: string = ''

   goalsTextValue: string = ''
   engagementTextValue: string = ''

   selectedLeaderAspirations: boolean | null = null
   selectedDeNovoCandidate: boolean | null = null

   recognitionTextValue: string = ''

   keyNeedsAndInterestsOptions: IDropdownOption[] = []
   keyNeedsAndInterestsSelectedValues: DVMToolSelectedOption[] = []

   challengesAndConcernsOptions: IDropdownOption[] = []
   challengesAndConcernsSelectedValues: DVMToolSelectedOption[] = []

   aspirationsAndGoalsOptions: IDropdownOption[] = []
   aspirationsAndGoalsSelectedValues: DVMToolSelectedOption[] = []

   engagementPlanOptions: IDropdownOption[] = []
   engagementPlanSelectedValues: DVMToolSelectedOption[] = []

   recognitionOptions: IDropdownOption[] = []
   recognitionSelectedValues: DVMToolSelectedOption[] = []

   keyNeedsAndInterestsRowData: DvmFeedbackRow = new DvmFeedbackRow()
   challengesAndConcernsRowData: DvmFeedbackRow = new DvmFeedbackRow()
   aspirationsAndGoalsRowData: DvmFeedbackRow = new DvmFeedbackRow()
   engagementPlanRowData: DvmFeedbackRow = new DvmFeedbackRow()
   recognitionAndAppreciationRowData: DvmFeedbackRow = new DvmFeedbackRow()
   dvmFeedbackRows: DvmFeedbackRow[] = []

   medicalInterestOptions: IDropdownOption[] = []
   medicalInterests: IDropdownOption[] = []
   medicalExpertise: IDropdownOption[] = []

   educationOptions: IDropdownOption[] = []

   selectedUniversity: IDropdownOption = {} as IDropdownOption

   gradYearInputValue: string

   historicalVersions: IDropdownOption[] = []
   formVersion: IDropdownOption = {} as IDropdownOption
   lastUpdatedDate: string

   plans: DVMPlan[]
   selectedOptions: DVMToolSelectedOption[]

   constructor(
      private dvmPlanClient: DVMPlanClient,
      private route: ActivatedRoute,
      private router: Router,
      private toastr: ToastrService,
      private dialog: MatDialog
   ) {
      const navState = this.router.getCurrentNavigation()?.extras.state
      if (navState) {
         this.lastUpdatedDate = navState['lastUpdated']
      }
   }

   ngOnInit(): void {
      this.dvmFeedbackRows = DVM_TOOL_JSON.dvmFeedbackRows.map((x) => new DvmFeedbackRow().init(x))
      this.keyNeedsAndInterestsRowData = this.dvmFeedbackRows[0]
      this.challengesAndConcernsRowData = this.dvmFeedbackRows[1]
      this.aspirationsAndGoalsRowData = this.dvmFeedbackRows[2]
      this.engagementPlanRowData = this.dvmFeedbackRows[3]
      this.recognitionAndAppreciationRowData = this.dvmFeedbackRows[4]

      this.dvmId = this.route.snapshot.paramMap.get('dvmId')

      if (this.dvmId === 'new') {
         this.route.queryParams.subscribe((params) => {
            const dvmUserId = params['userId']

            if (dvmUserId) {
               console.log('dvmUserId from query params:', dvmUserId)

               this.startNewPlanForDVM(dvmUserId)
            }
         })
      } else {
         this.getDVMPlansForDVM(Number(this.dvmId))
      }

      setTimeout(() => {
         this.populateDropDowns()
      }, 500)
   }

   public startNewPlanForDVM(dvmId: number) {
      this.dvmPlanClient.getDVM(dvmId).subscribe({
         next: (data) => {
            this.dvm = data

            this.dvmPlan = {
               dvmName: this.dvm.dvmDisplayName,
               dvmId: this.dvm.dvmUserId,
               practiceName: this.dvm.practiceName,
               rmName: this.dvm.rmDisplayName,
               cosaName: this.dvm.cosDisplayName,
               communicationPreference: [],
            }
         },
         error: (error) => {
            console.error('Error fetching batch data:', error)
         },
      })
   }

   public getDVMPlansForDVM(dvmId: number) {
      forkJoin({
         plans: this.dvmPlanClient.getDVMPlansForDVM(dvmId),
         selectedOptions: this.dvmPlanClient.getDvmToolSelectedOptions(dvmId),
      }).subscribe({
         next: ({ plans, selectedOptions }) => {
            this.plans = plans
            this.selectedOptions = selectedOptions
            this.loadDvmPlan()
         },
         error: (err) => {
            console.error('Error fetching DVM data:', err)
         },
      })
   }

   public loadDvmPlan() {
      this.dvmPlans = this.plans

      if (this.plans?.length > 0) {
         this.dvmPlan =
            this.plans
               .sort((a, b) => dayjs.utc(b.dateUpdated).local().toDate().getTime() - dayjs.utc(a.dateUpdated).local().toDate().getTime())
               .find(
                  (plan) =>
                     dayjs.utc(plan.dateUpdated).local().format('MM/DD/YYYY') + ' ' + dayjs.utc(plan.dateUpdated).local().format('hh:mm A') ===
                     dayjs.utc(this.lastUpdatedDate).local().format('MM/DD/YYYY') + ' ' + dayjs.utc(this.lastUpdatedDate).local().format('hh:mm A')
               ) ?? this.plans[0]

         this.historicalVersions = []
         for (let i = 0; i < this.plans.length; i++) {
            const date = this.plans[i].dateUpdated
            if (!date) continue

            const localDate = dayjs.utc(date).local()
            const formattedDate = localDate.format('MM/DD/YYYY')
            const formattedTime = localDate.format('hh:mm A')
            const dateStr = formattedDate + ' ' + formattedTime
            if (!this.historicalVersions.find((x) => x.name === dateStr)) {
               this.historicalVersions.push({ id: this.historicalVersions.length + 1, name: dateStr })
            }
         }
         this.historicalVersions = this.historicalVersions.sort((a, b) => dayjs.utc(b.name).toDate().getTime() - dayjs.utc(a.name).toDate().getTime())
         this.formVersion = this.historicalVersions.find((x) => x.name === this.lastUpdatedDate) ?? this.historicalVersions[0]

         const p = this.dvmPlan

         this.emailInputValue = p.dvmEmail
         this.phoneNumberInputValue = p.dvmPhone

         this.selectedYearsExperience = String(p.yearsExperience)
         this.selectedLendAHand = p.lendAHandInterest

         this.interestsTextValue = p.dvmInterests
         this.concernsTextValue = p.dvmConcerns

         this.goalsTextValue = p.dvmGoals
         this.engagementTextValue = p.dvmEngagement

         this.selectedLeaderAspirations = p.leaderAsprations
         this.selectedDeNovoCandidate = p.deNovoCandidate

         this.recognitionTextValue = p.dvmRecognition

         this.gradYearInputValue = p.gradYear

         this.keyNeedsAndInterestsRowData.loadInputData(this.dvmPlan.id, this.interestsTextValue, this.selectedOptions, DVMToolSelectionType.KeyNeeds)
         this.aspirationsAndGoalsRowData.loadInputData(this.dvmPlan.id, this.goalsTextValue, this.selectedOptions, DVMToolSelectionType.Aspirations)
         this.challengesAndConcernsRowData.loadInputData(this.dvmPlan.id, this.concernsTextValue, this.selectedOptions, DVMToolSelectionType.Concerns)
         this.recognitionAndAppreciationRowData.loadInputData(
            this.dvmPlan.id,
            this.recognitionTextValue,
            this.selectedOptions,
            DVMToolSelectionType.Recognition
         )
         this.engagementPlanRowData.loadInputData(
            this.dvmPlan.id,
            this.engagementTextValue,
            this.selectedOptions,
            DVMToolSelectionType.ShortTermActions
         )
         this.loadMedicalInterests(this.dvmPlan.id, this.selectedOptions)
         this.loadSelectedUniversity(this.dvmPlan.id, this.selectedOptions)

         this.selectedCommunicationPreferences = p.communicationPreference ?? []
      } else {
         this.startNewPlanForDVM(Number(this.dvmId))
      }
   }

   public loadMedicalInterests(dvmId: number, selectedOptions: DVMToolSelectedOption[]) {
      const medicalInterestDropdownOptions = selectedOptions.filter(
         (x) => x.dvmEngagementId === dvmId && x.dvmToolSelection.dvmToolSelectionTypeID === DVMToolSelectionType.MedicalInterests
      )
      this.medicalInterests = medicalInterestDropdownOptions
         .filter((option) => option?.medExpertise !== true) // treat null/0 as "interests"
         .map((option) => ({
            id: option.dvmToolSelectionId,
            name: option.dvmToolSelection.description,
            medExpertise: false,
         }))

      this.medicalExpertise = medicalInterestDropdownOptions
         .filter((option) => option?.medExpertise === true)
         .map((option) => ({
            id: option.dvmToolSelectionId,
            name: option.dvmToolSelection.description,
            medExpertise: true,
         }))
   }

   public loadSelectedUniversity(dvmId: number, selectedOptions: DVMToolSelectedOption[]) {
      this.selectedUniversity = selectedOptions
         .filter((x) => x.dvmEngagementId === dvmId && x.dvmToolSelection.dvmToolSelectionTypeID === DVMToolSelectionType.Education)
         .map((option) => {
            return { id: option.dvmToolSelectionId, name: option.dvmToolSelection.description }
         })[0]
   }

   public saveDVMPlan() {
      this.goalsTextValue = this.aspirationsAndGoalsRowData.textareaValue
      this.concernsTextValue = this.challengesAndConcernsRowData.textareaValue
      this.interestsTextValue = this.keyNeedsAndInterestsRowData.textareaValue
      this.engagementTextValue = this.engagementPlanRowData.textareaValue
      this.recognitionTextValue = this.recognitionAndAppreciationRowData.textareaValue

      if (
         !this.emailInputValue ||
         !this.phoneNumberInputValue ||
         this.medicalInterests.length === 0 ||
         this.medicalExpertise.length === 0 ||
         !this.gradYearInputValue ||
         !this.selectedUniversity?.id ||
         !this.goalsTextValue ||
         !this.concernsTextValue ||
         !this.interestsTextValue ||
         !this.engagementTextValue ||
         !this.recognitionTextValue
      ) {
         this.toastr.error('Please fill out all required fields before saving.')
         return
      }

      if (!this.keyNeedsAndInterestsRowData.selectedItems.length || this.keyNeedsAndInterestsRowData.selectedItems.some((x) => !x || !x.id)) {
         this.toastr.error('Please complete all Key Needs & Interests dropdowns.')
         return
      }

      if (!this.challengesAndConcernsRowData.selectedItems.length || this.challengesAndConcernsRowData.selectedItems.some((x) => !x || !x.id)) {
         this.toastr.error('Please complete all Challenges & Concerns dropdowns.')
         return
      }

      if (!this.aspirationsAndGoalsRowData.selectedItems.length || this.aspirationsAndGoalsRowData.selectedItems.some((x) => !x || !x.id)) {
         this.toastr.error('Please complete all Aspirations & Goals dropdowns.')
         return
      }

      if (!this.engagementPlanRowData.selectedItems.length || this.engagementPlanRowData.selectedItems.some((x) => !x || !x.id)) {
         this.toastr.error('Please complete all Engagement Plan dropdowns.')
         return
      }

      if (
         !this.recognitionAndAppreciationRowData.selectedItems.length ||
         this.recognitionAndAppreciationRowData.selectedItems.some((x) => !x || !x.id)
      ) {
         this.toastr.error('Please complete all Recognition & Appreciation dropdowns.')
         return
      }

      var dvmPlanToSave: DVMPlan = { ...this.dvmPlan }
      dvmPlanToSave.dateUpdated = dayjs.utc(new Date()).local().toDate()

      dvmPlanToSave.communicationPreference = this.selectedCommunicationPreferences
      dvmPlanToSave.dvmEmail = this.emailInputValue
      dvmPlanToSave.dvmPhone = this.phoneNumberInputValue

      dvmPlanToSave.yearsExperience = Number(this.selectedYearsExperience)
      dvmPlanToSave.lendAHandInterest = this.selectedLendAHand

      dvmPlanToSave.dvmInterests = this.interestsTextValue
      dvmPlanToSave.dvmConcerns = this.concernsTextValue

      dvmPlanToSave.dvmGoals = this.goalsTextValue
      dvmPlanToSave.dvmEngagement = this.engagementTextValue

      dvmPlanToSave.leaderAsprations = this.selectedLeaderAspirations
      dvmPlanToSave.deNovoCandidate = this.selectedDeNovoCandidate

      dvmPlanToSave.dvmRecognition = this.recognitionTextValue

      dvmPlanToSave.gradYear = this.gradYearInputValue

      console.log('Saving Plan:')

      this.dvmPlanClient
         .saveDvmPlan(dvmPlanToSave)
         .pipe(
            switchMap((dvmPlan) => {
               console.log('✅ SAVED DVM Plan:', dvmPlan)
               this.toastr.success('Your plan was saved successfully.')

               const saveDropdownOptionsRequest: DVMToolSelectedOption[] = [
                  ...this.keyNeedsAndInterestsRowData.selectedItems
                     .filter((x) => x)
                     .map((option) => {
                        return { dvmEngagementId: dvmPlan, dvmToolSelectionId: option.id, medExpertise: false }
                     }),
                  ...this.challengesAndConcernsRowData.selectedItems
                     .filter((x) => x)
                     .map((option) => {
                        return { dvmEngagementId: dvmPlan, dvmToolSelectionId: option.id, medExpertise: false }
                     }),
                  ...this.aspirationsAndGoalsRowData.selectedItems
                     .filter((x) => x)
                     .map((option) => {
                        return { dvmEngagementId: dvmPlan, dvmToolSelectionId: option.id, medExpertise: false }
                     }),
                  ...this.engagementPlanRowData.selectedItems
                     .filter((x) => x)
                     .map((option) => {
                        return { dvmEngagementId: dvmPlan, dvmToolSelectionId: option.id, medExpertise: false }
                     }),
                  ...this.recognitionAndAppreciationRowData.selectedItems
                     .filter((x) => x)
                     .map((option) => {
                        return { dvmEngagementId: dvmPlan, dvmToolSelectionId: option.id, medExpertise: false }
                     }),
                  ...this.medicalInterests
                     .filter((x) => x)
                     .map((option) => {
                        return { dvmEngagementId: dvmPlan, dvmToolSelectionId: option.id, medExpertise: false }
                     }),
                  ...this.medicalExpertise
                     .filter((x) => x)
                     .map((option) => {
                        return { dvmEngagementId: dvmPlan, dvmToolSelectionId: option.id, medExpertise: true }
                     }),
               ]

               if (this.selectedUniversity?.id > 0) {
                  saveDropdownOptionsRequest.push({ dvmEngagementId: dvmPlan, dvmToolSelectionId: this.selectedUniversity.id, medExpertise: false })
               }

               return forkJoin({
                  dvmPlan: of(dvmPlan), // carry the already-saved plan forward
                  dropdowns: this.dvmPlanClient.saveDvmToolSelectionOptions(saveDropdownOptionsRequest),
               })
            })
         )
         .subscribe({
            next: ({ dvmPlan, dropdowns }) => {
               console.log('✅ SAVED DVM DROPDOWN OPTIONS:', dropdowns)
               this.lastUpdatedDate =
                  dayjs.utc(dvmPlanToSave.dateUpdated).local().format('MM/DD/YYYY') +
                  ' ' +
                  dayjs.utc(dvmPlanToSave.dateUpdated).local().format('hh:mm A')
               this.getDVMPlansForDVM(Number(this.dvmId))
            },
            error: (err) => {
               console.error('❌ Failed during save operation:', err)
            },
         })
   }

   onClickSave(): void {
      console.log('CLICKED SAVE')
      this.saveDVMPlan()
   }

   populateDropDowns(): void {
      this.dvmPlanClient.getDvmToolSelection(DVMToolSelectionType.KeyNeeds).subscribe({
         next: (val: any) => {
            this.keyNeedsAndInterestsOptions = val.map((item) => {
               return { id: item.id, name: item.description }
            })

            this.keyNeedsAndInterestsRowData.options = this.keyNeedsAndInterestsOptions
         },
      })

      this.dvmPlanClient.getDvmToolSelection(DVMToolSelectionType.Concerns).subscribe({
         next: (val: any) => {
            this.challengesAndConcernsOptions = val.map((item) => {
               return { id: item.id, name: item.description }
            })

            this.challengesAndConcernsRowData.options = this.challengesAndConcernsOptions
         },
      })

      this.dvmPlanClient.getDvmToolSelection(DVMToolSelectionType.Aspirations).subscribe({
         next: (val: any) => {
            this.aspirationsAndGoalsOptions = val.map((item) => {
               return { id: item.id, name: item.description }
            })

            this.aspirationsAndGoalsRowData.options = this.aspirationsAndGoalsOptions
         },
      })

      this.dvmPlanClient.getDvmToolSelection(DVMToolSelectionType.ShortTermActions).subscribe({
         next: (val: any) => {
            this.engagementPlanOptions = val.map((item) => {
               return { id: item.id, name: item.description }
            })

            this.engagementPlanRowData.options = this.engagementPlanOptions
         },
      })

      this.dvmPlanClient.getDvmToolSelection(DVMToolSelectionType.Recognition).subscribe({
         next: (val: any) => {
            this.recognitionOptions = val.map((item) => {
               return { id: item.id, name: item.description }
            })

            this.recognitionAndAppreciationRowData.options = this.recognitionOptions
         },
      })

      this.dvmPlanClient.getDvmToolSelection(DVMToolSelectionType.Education).subscribe({
         next: (val: any) => {
            this.educationOptions = val.map((item) => {
               return { id: item.id, name: item.description }
            })
         },
      })

      this.dvmPlanClient.getDvmToolSelection(DVMToolSelectionType.MedicalInterests).subscribe({
         next: (val: any) => {
            this.medicalInterestOptions = val.map((item) => {
               return { id: item.id, name: item.description, medExpertise: false }
            })
         },
      })
   }

   onCommunicationCheckboxChange(value: string, checked: boolean) {
      if (checked) {
         if (!this.selectedCommunicationPreferences.includes(value)) {
            this.selectedCommunicationPreferences.push(value)
         }
      } else {
         this.selectedCommunicationPreferences = this.selectedCommunicationPreferences.filter((v) => v !== value)
      }
   }

   handleMedicalInterestsChanged(selection: IDropdownOption[]) {
      this.medicalInterests = selection
   }

   handleMedicalExpertiseChanged(selection: IDropdownOption[]) {
      this.medicalExpertise = selection
   }

   handleUniversityChanged(event: IDropdownOption) {
      this.selectedUniversity = event
   }

   handleVersionChanged(selection: IDropdownOption) {
      this.lastUpdatedDate = selection.name
      this.loadDvmPlan()
   }

   getLastSavedDatetime(): string {
      const localDate = dayjs.utc(this.dvmPlan.dateUpdated).local()
      return localDate.format('MM/DD/YYYY') + ' ' + localDate.format('h:mm A')
   }

   openMeetingSchedulerModal(): void {
      this.dialog.open(MeetingSchedulerModalComponent, {
         panelClass: 'booking-drawer-panel',
         autoFocus: false,
         disableClose: true,
         data: {
            rmName: this.dvm.dvmDisplayName,
            subject: 'DVM Engagement Meeting',
         },
      })
   }
}
